import {Injectable} from '@angular/core';
import {ValidatorFn, Validators} from '@angular/forms';

@Injectable({providedIn: 'root'})
export class ValidationInputService {

	// noinspection JSMethodCanBeStatic

	/**
	 * Le pattern pour toutes les saisies de désignation, description, etc
	 */
	public readonly patternTexte: string = '[!-~À-ÂÇ-ÏÒ-ÖÙ-Üà-äç-öù-ý«»°€ ]+';

	/**
	 * Le pattern pour les nombres décimaux.
	 */
	public readonly patternDecimal: string = '^\\d{1,15}(\\.\\d{1,4})?$';

	/**
	 * Le pattern pour les nombres entiers.
	 */
	public readonly patternEntier: string = '^\\d{1,15}$';

	/**
	 * Le pattern pour valider un code article.
	 */
	public readonly patternCodeArticle: string = '[a-zA-Z0-9]{4}';

	/**
	 * Le pattern pour valider un caractère de code article.
	 */
	public readonly patternCaractereCodeArticle: string = '[a-zA-Z0-9]';

	/**
	 * Le pattern pour valider un caractère de code.
	 */
	public readonly patternCode: string = '[a-zA-Z0-9_-]+';

	/**
	 * Le pattern pour valider un email.
	 */
	public readonly patternEmail: string = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';

	/**
	 * Retourne un validator validant un nombre décimal avec le nombre de décimales max spécifié
	 * @param nbDecimales le nombre de décimales
	 * @param negatifAutorise indique si les nombres négatifs sont autorisés.
	 */
	static validatorDecimal(nbDecimales: number, negatifAutorise: boolean): ValidatorFn {
		let pattern = '[0-9]+([,.][0-9]{0,' + nbDecimales + '})?$';
		if (negatifAutorise) {
			pattern = '^-?' + pattern;
		}
		else {
			pattern = '^' + pattern;
		}
		return Validators.pattern(pattern);
	}

	/**
	 * Retourne un validator validant un nombre avec 2 décimales max
	 * @param negatifAutorise indique si les nombres négatifs sont autorisés.
	 */
	static validator2Decimal(negatifAutorise: boolean = true): ValidatorFn {
		return this.validatorDecimal(2, negatifAutorise);
	}

	/**
	 * Retourne un validator validant un nombre avec 3 décimales max
	 * @param negatifAutorise indique si les nombres négatifs sont autorisés.
	 */
	static validator3Decimal(negatifAutorise: boolean = true): ValidatorFn {
		return this.validatorDecimal(3, negatifAutorise);
	}

	/**
	 * Retourne un validator validant un nombre entier
	 */
	static validatorEntier(): ValidatorFn {
		return Validators.pattern('^[0-9]*$');
	}

	static validatorDate(): ValidatorFn {
		return Validators.pattern(/^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/);
	}

	/**
	 * Teste un caractère sur le pattern de saisie autorisé.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 */
	public estCaractereValidePourTexte(caractere: string): boolean {
		return !!caractere.match(this.patternTexte);
	}

	/**
	 * Teste un caractère sur le pattern de saisie autorisé.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 */
	public estCaractereValidePourEntier(caractere: string): boolean {
		return !!caractere.match('[0-9]');
	}

	/**
	 * Teste un caractère sur le pattern de saisie autorisé.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 * @deprecated cette méthode a été modifié dans l'application Sigale Magasin (qui ne l'utilise plus).
	 * Il faudrait voir pourquoi il y a une différence avec la méthode estCaractereValidePourEntier
	 */
	public estCaracterereValidePourDecimal(caractere: string): boolean {
		return !!caractere.match('[0-9]+|\\.|\\,');
	}

	/**
	 * Teste un caractère sur le pattern de saisie autorisé.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 */
	public estCaractereValidePourDecimal(caractere: string): boolean {
		return !!caractere.match('[0-9]|\\.|\\,');
	}

	/**
	 * Teste un caractère sur le pattern autorisé du code article.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 */
	public estCaractereValidePourCodeArticle(caractere: string): boolean {
		return !!caractere.match(this.patternCaractereCodeArticle);
	}

	/**
	 * Teste un caractère sur le pattern autorisé du code.
	 * @param {string} caractere le caractère à tester.
	 * @returns {boolean} true si le caractère est valide.
	 */
	public estCaractereValidePourCode(caractere: string): boolean {
		return !!caractere.match(this.patternCode);
	}

	/**
	 * Teste une chaine sur le pattern autorisé des emails.
	 * @param {string} chaine la chaine à tester.
	 * @returns {boolean} true si la chaine est valide.
	 */
	public estChaineValidePourEmail(chaine: string): boolean {
		return !!chaine.match(this.patternEmail);
	}
}
